import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import objectsCategoriesService from "services/objectsCategories.service"
import type { TSimpleCategory, TDualCategory, TCompositeCategory, TColumnBasedCategory } from "types/configuration.type"

// Creating or deleting an object moves model numbers in or out of the "Without category" pool
// the create-object form offers (#940). Not awaited, so the caller's onSuccess isn't held by the refetch.
const useInvalidateModelNumbersOnSuccess = () => {
	const queryClient = useQueryClient()
	return () => {
		void queryClient.invalidateQueries({ queryKey: ["getModelNumbers"] })
	}
}

export const useObjectsCategories = (filters: { filterAtomic?: boolean } = {}) => {
	return useQuery({
		queryKey: ["objectsCategories", filters],
		queryFn: () => objectsCategoriesService.getObjectsCategories(filters),
		select: ({ data }) => data,
	})
}

export const useIncludeInProcessing = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: ["includeInProcessing"],
		mutationFn: ({ includedInProcessing, categoryId }: { categoryId: number; includedInProcessing: boolean }) =>
			objectsCategoriesService.includeInProcessing(includedInProcessing, categoryId),
		onSuccess: async () => {
			// Invalidate and refetch in the background after mutation succeeds
			await queryClient.invalidateQueries({ queryKey: ["objectsCategories"] })
		},
	})
}

export const useCreateSimpleCategory = () => {
	const onSuccess = useInvalidateModelNumbersOnSuccess()

	return useMutation({
		mutationKey: ["createSimpleCategory"],
		onSuccess,
		mutationFn: ({ name, label, superCategory, modelNumbers }: TSimpleCategory) =>
			objectsCategoriesService.createSimpleCategory(name, label, superCategory, modelNumbers),
	})
}

export const useCreateDualCategory = () => {
	const onSuccess = useInvalidateModelNumbersOnSuccess()

	return useMutation({
		mutationKey: ["createDualCategory"],
		onSuccess,
		mutationFn: ({
			name,
			label,
			topLevelCategory,
			superCategory,
			leftModelNumbers,
			rightModelNumbers,
			wholeModelNumbers,
		}: TDualCategory) =>
			objectsCategoriesService.createDualCategory(
				name,
				label,
				topLevelCategory,
				superCategory,
				leftModelNumbers,
				rightModelNumbers,
				wholeModelNumbers,
			),
	})
}

export const useCreateCompositeCategory = () => {
	const onSuccess = useInvalidateModelNumbersOnSuccess()

	return useMutation({
		mutationKey: ["createCompositeCategory"],
		onSuccess,
		mutationFn: ({ name, label, superCategory, modelNumbers, variants }: TCompositeCategory) =>
			objectsCategoriesService.createCompositeCategory(name, label, superCategory, modelNumbers, variants),
	})
}

export const useCreateColumnBasedCategory = () => {
	const onSuccess = useInvalidateModelNumbersOnSuccess()

	return useMutation({
		mutationKey: ["createColumnBasedCategory"],
		onSuccess,
		mutationFn: ({ name, label, superCategory, columns, modelNumbers }: TColumnBasedCategory) => {
			const minColumns = columns[0]
			const maxColumns = columns[1]
			return objectsCategoriesService.createColumnBasedCategory(
				name,
				label,
				superCategory,
				minColumns,
				maxColumns,
				modelNumbers,
			)
		},
	})
}

export const useUpdateModelNumbers = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: ["updateModelNumbers"],
		mutationFn: ({ categoryId, modelNumberIds }: { categoryId: number; modelNumberIds: number[] }) =>
			objectsCategoriesService.updateModelNumbers(categoryId, modelNumberIds),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["objectsCategories"] }),
				queryClient.invalidateQueries({ queryKey: ["getModelNumbers"] }),
			])
		},
	})
}

export const useDeleteCategory = () => {
	const onSuccess = useInvalidateModelNumbersOnSuccess()

	return useMutation({
		mutationKey: ["deleteCategory"],
		onSuccess,
		mutationFn: (id: number | undefined) => objectsCategoriesService.deleteCategory(id),
	})
}

export const useUpdateModelNumbersOrder = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: ["updateModelNumbersOrder"],
		mutationFn: ({ categoryId, modelNumberIds }: { categoryId: number; modelNumberIds: number[] }) =>
			objectsCategoriesService.updateModelNumbersOrder(categoryId, modelNumberIds),
		// Not awaited: the save is done once the PUT lands; the list refetch must not hold "Saving…".
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["objectsCategories"] })
		},
	})
}
